import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useLang, useT } from '../../i18n';
import type { CocoHomeDevice } from '../../types/cocohome';
import type { HealthSyncStatus } from '../../types/healthSync';

export default function HealthArchiveStatus({ devices }: { devices: CocoHomeDevice[] }) {
  const t = useT();
  const { lang } = useLang();
  const [status, setStatus] = useState<HealthSyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true); setError(false);
    try { const next = await api.cocoHome.healthStatus(signal); if (!signal?.aborted) setStatus(next); }
    catch { if (!signal?.aborted) { setError(true); setStatus(null); } }
    finally { if (!signal?.aborted) setLoading(false); }
  }, []);
  useEffect(() => { const controller = new AbortController(); void load(controller.signal); return () => controller.abort(); }, [load]);
  const date = (value: string) => new Date(value).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US');
  const deviceName = (id: string) => devices.find(device => device.device_id === id)?.device_name ?? id;
  return <section aria-labelledby="health-archive-title" className="rounded-lg border border-neutral-200 bg-white p-5 shadow-soft md:p-6">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><h3 id="health-archive-title" className="font-mono text-sm text-cocoa">{t('healthArchive.title')}</h3>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-coffee/80">{t('healthArchive.description')}</p></div>
      <button type="button" disabled={loading} onClick={() => void load()} className="rounded border border-neutral-200 px-3 py-2 text-sm text-cocoa disabled:opacity-50">{t(loading ? 'healthArchive.loading' : 'healthArchive.refresh')}</button>
    </div>
    {error && <p role="alert" className="mt-4 text-sm text-red-700">{t('healthArchive.error')}</p>}
    {status && <div className="mt-5 space-y-4" aria-live="polite">
      <p className={`rounded-md px-3 py-2 text-sm ${status.archive_key_registered ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>{t(status.archive_key_registered ? 'healthArchive.ready' : 'healthArchive.notReady')}</p>
      <p className="text-xs leading-relaxed text-coffee/70">{t('healthArchive.timing')}</p>
      {status.devices.length === 0 ? <p className="text-sm text-coffee">{t('healthArchive.empty')}</p> : <div className="grid gap-3 lg:grid-cols-2">
        {status.devices.map(device => {
          const receipt = device.latest_receipt;
          return <article key={`${device.principal_id}:${device.device_id}`} className="min-w-0 rounded-lg border border-neutral-200 p-4">
            <h4 className="break-words text-sm font-semibold text-cocoa">{deviceName(device.device_id)}</h4>
            <dl className="mt-3 grid grid-cols-3 gap-3 text-sm">
              {(['queued_count', 'archived_count', 'retry_count'] as const).map(key => <div key={key}><dt className="text-xs text-coffee/70">{t(`healthArchive.${key}`)}</dt><dd className="mt-1 font-mono text-cocoa">{device[key].toLocaleString()}</dd></div>)}
            </dl>
            {device.last_error_code && <p className="mt-3 text-xs text-amber-800">{t('healthArchive.archiveRetry')}</p>}
            {device.retry_count > 0 && <p className="mt-3 text-xs text-amber-800">{t('healthArchive.retryHint')}</p>}
            <p className="mt-3 text-xs text-coffee/70">{t('healthArchive.latestUpload')}: {date(device.latest_upload_at)}</p>
            <p className="mt-1 text-xs text-coffee/70">{t('healthArchive.latestArchive')}: {receipt ? date(receipt.archived_at) : t('healthArchive.waiting')}</p>
          </article>;
        })}
      </div>}
      <p className="text-xs leading-relaxed text-coffee/70">{t('healthArchive.receiptHint', { days: status.pending_retention_days })}</p>
    </div>}
  </section>;
}
